import { usePageTitle } from "@/hooks/use-page-title";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Mail, Clock, Shield, FileText, Save, Loader2, AlertTriangle, Info, Webhook, Eye, EyeOff, Copy, Check } from "lucide-react";
import { MarkdownBodyEditor } from "@/components/markdown-body-editor";

interface EmailScheduleSettings {
  sendHour: number;
  sendMinute: number;
  timezone: string;
}

interface TestModeSettings {
  enabled: boolean;
  allowedDomains: string[];
}

interface EmailTemplateSettings {
  subject: string;
  body: string;
}

interface WelcomeEmailTemplateSettings {
  subject: string;
  body: string;
}

interface WatchlistEmailTemplateSettings {
  subject: string;
  body: string;
}

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "UTC", label: "UTC" },
];

const MERGE_FIELDS = [
  { field: "{{contactName}}", description: "Recipient contact name" },
  { field: "{{carrierName}}", description: "Carrier company name" },
  { field: "{{docketPrefix}}", description: "Docket prefix (MC/FF/MX)" },
  { field: "{{docketNumber}}", description: "Docket number" },
  { field: "{{workOrderNumber}}", description: "Work order number" },
  { field: "{{authorityType}}", description: "Authority type code (e.g., CMO)" },
  { field: "{{authorityTypeDescription}}", description: "Authority type description (e.g., Common carrier of property)" },
  { field: "{{letterType}}", description: "Letter type (e.g., Authority Grant, Reinstatement)" },
  { field: "{{serviceDate}}", description: "Service date" },
  { field: "{{#each letters}}...{{/each}}", description: "Loop through letters" },
];

const WELCOME_EMAIL_MERGE_FIELDS = [
  { field: "{{firstName}}", description: "User's first name" },
  { field: "{{lastName}}", description: "User's last name" },
  { field: "{{email}}", description: "User's email address" },
  { field: "{{companyName}}", description: "Company name (if applicable)" },
  { field: "{{loginUrl}}", description: "Login/invite acceptance URL" },
  { field: "{{expirationHours}}", description: "Hours until link expires (72)" },
];

const WATCHLIST_EMAIL_MERGE_FIELDS = [
  { field: "{{contactName}}", description: "Recipient contact name" },
  { field: "{{carrierName}}", description: "Carrier / company name" },
  { field: "{{docketPrefix}}", description: "Docket prefix (MC, FF, MX)" },
  { field: "{{docketNumber}}", description: "Docket number" },
  { field: "{{workOrderNumber}}", description: "Work order number" },
  { field: "{{ageDays}}", description: "Watchlist age in days" },
  { field: "{{createdDate}}", description: "Date watchlist was created (MM/DD/YYYY)" },
  { field: "{{canceledDate}}", description: "Date watchlist was canceled — dismissal only" },
  { field: "{{expiredDate}}", description: "Date watchlist expired — dismissal only" },
  { field: "{{watchlistType}}", description: "Watch type (Single Match / Continuous)" },
  { field: "{{watchlistDays}}", description: "Number of watch days" },
  { field: "{{letterType}}", description: "Letter type filter" },
  { field: "{{matchCount}}", description: "Number of matches found" },
  { field: "{{status}}", description: "Current watchlist status" },
  { field: "{{targetIdentifier}}", description: "Raw watchlist target identifier" },
  { field: "{{#if field}}...{{/if}}", description: "Conditional block (only renders if field is non-empty)" },
];


export default function SettingsPage() {
  usePageTitle("Settings");
  const { toast } = useToast();
  const { isAuthenticated, isLoading: authLoading, user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [emailSchedule, setEmailSchedule] = useState<EmailScheduleSettings>({
    sendHour: 7,
    sendMinute: 0,
    timezone: "America/Chicago",
  });

  const [testMode, setTestMode] = useState<TestModeSettings>({
    enabled: true,
    allowedDomains: ["@donnormanassociates.com", "@threadedsolutions.com"],
  });

  const [emailTemplate, setEmailTemplate] = useState<EmailTemplateSettings>({
    subject: "Authority Letter Notification - {{workOrderNumber}}",
    body: "",
  });

  const [welcomeEmailTemplate, setWelcomeEmailTemplate] = useState<WelcomeEmailTemplateSettings>({
    subject: "Welcome to DNA Authority Management - Your Account is Ready",
    body: `Dear {{firstName}},

Welcome to DNA Authority Management! Your account has been created and is ready for use.

Click the link below to set up your password and access your account:
{{loginUrl}}

This link will expire in {{expirationHours}} hours.

If you have any questions, please contact your administrator.

Best regards,
DNA Authority Management Team`,
  });

  const [watchlistReminderTemplate, setWatchlistReminderTemplate] = useState<WatchlistEmailTemplateSettings>({
    subject: "Watchlist Reminder - {{targetIdentifier}} ({{workOrderNumber}})",
    body: "",
  });

  const [watchlistDismissalTemplate, setWatchlistDismissalTemplate] = useState<WatchlistEmailTemplateSettings>({
    subject: "Watchlist Closed - {{targetIdentifier}} ({{workOrderNumber}})",
    body: "",
  });

  const [externalWatchlistDays, setExternalWatchlistDays] = useState<string>("100");
  const [internalWatchlistDays, setInternalWatchlistDays] = useState<string>("30");
  const [externalWatchlistDaysError, setExternalWatchlistDaysError] = useState<string>("");
  const [internalWatchlistDaysError, setInternalWatchlistDaysError] = useState<string>("");

  const [newDomain, setNewDomain] = useState("");
  const [showWebhookUrl, setShowWebhookUrl] = useState(false);
  const [webhookCopied, setWebhookCopied] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, authLoading, toast]);

  const isAdmin = currentUser?.role === 'admin';

  const { data: settings, isLoading } = useQuery<Record<string, any>>({
    queryKey: ['/api/settings'],
    enabled: isAuthenticated && isAdmin,
    refetchOnMount: "always",
  });

  const { data: webhookInfo } = useQuery<{ webhookUrl: string; environment: string; webhookSecretConfigured: boolean }>({
    queryKey: ['/api/settings/webhook-info'],
    enabled: isAuthenticated && isAdmin,
  });

  useEffect(() => {
    if (settings) {
      if (settings.email_schedule) {
        setEmailSchedule(settings.email_schedule);
      }
      if (settings.email_test_mode) {
        setTestMode(settings.email_test_mode);
      }
      if (settings.email_template) {
        setEmailTemplate(settings.email_template);
      }
      if (settings.welcome_email_template) {
        setWelcomeEmailTemplate(settings.welcome_email_template);
      }
      if (settings.watchlist_reminder_template) {
        setWatchlistReminderTemplate(settings.watchlist_reminder_template);
      }
      if (settings.watchlist_dismissal_template) {
        setWatchlistDismissalTemplate(settings.watchlist_dismissal_template);
      }
      if (settings.external_watchlist_default_days != null) {
        setExternalWatchlistDays(String(settings.external_watchlist_default_days));
      }
      if (settings.internal_watchlist_default_days != null) {
        setInternalWatchlistDays(String(settings.internal_watchlist_default_days));
      }
    }
  }, [settings]);

  const updateSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const response = await apiRequest("PUT", `/api/settings/${key}`, { value });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/settings'] });
      toast({
        title: "Settings saved",
        description: "Your settings have been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save settings",
        variant: "destructive",
      });
    },
  });

  const handleSaveSchedule = () => {
    updateSettingMutation.mutate({ key: "email_schedule", value: emailSchedule });
  };

  const handleSaveTestMode = () => {
    updateSettingMutation.mutate({ key: "email_test_mode", value: testMode });
  };

  const handleSaveTemplate = () => {
    updateSettingMutation.mutate({ key: "email_template", value: emailTemplate });
  };

  const handleSaveWelcomeTemplate = () => {
    updateSettingMutation.mutate({ key: "welcome_email_template", value: welcomeEmailTemplate });
  };

  const handleSaveWatchlistReminderTemplate = () => {
    updateSettingMutation.mutate({ key: "watchlist_reminder_template", value: watchlistReminderTemplate });
  };

  const handleSaveWatchlistDismissalTemplate = () => {
    updateSettingMutation.mutate({ key: "watchlist_dismissal_template", value: watchlistDismissalTemplate });
  };

  const handleSaveExternalWatchlistDays = () => {
    const num = Number(externalWatchlistDays);
    if (!Number.isInteger(num) || num < 1) {
      setExternalWatchlistDaysError("Must be a positive integer (≥1).");
      return;
    }
    setExternalWatchlistDaysError("");
    updateSettingMutation.mutate({ key: "external_watchlist_default_days", value: num });
  };

  const handleSaveInternalWatchlistDays = () => {
    const num = Number(internalWatchlistDays);
    if (!Number.isInteger(num) || num < 1) {
      setInternalWatchlistDaysError("Must be a positive integer (≥1).");
      return;
    }
    setInternalWatchlistDaysError("");
    updateSettingMutation.mutate({ key: "internal_watchlist_default_days", value: num });
  };

  const addDomain = () => {
    if (newDomain && !testMode.allowedDomains.includes(newDomain)) {
      const domain = newDomain.startsWith("@") ? newDomain : `@${newDomain}`;
      setTestMode({
        ...testMode,
        allowedDomains: [...testMode.allowedDomains, domain],
      });
      setNewDomain("");
    }
  };

  const removeDomain = (domain: string) => {
    setTestMode({
      ...testMode,
      allowedDomains: testMode.allowedDomains.filter(d => d !== domain),
    });
  };

  const copyWebhookUrl = async () => {
    if (webhookInfo?.webhookUrl) {
      await navigator.clipboard.writeText(webhookInfo.webhookUrl);
      setWebhookCopied(true);
      toast({
        title: "Copied",
        description: "Webhook URL copied to clipboard",
      });
      setTimeout(() => setWebhookCopied(false), 2000);
    }
  };

  const maskUrl = (url: string) => {
    if (!url) return "";
    const parts = url.split("/api/");
    if (parts.length === 2) {
      return parts[0].substring(0, 20) + "••••••••••" + "/api/" + parts[1];
    }
    return url.substring(0, 20) + "••••••••••";
  };

  if (authLoading || isLoading) {
    return (
      <ResponsiveLayout title="Admin Settings" subtitle="Configure system-wide settings and preferences">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ResponsiveLayout>
    );
  }

  if (!isAdmin) {
    return (
      <ResponsiveLayout title="Admin Settings" subtitle="Configure system-wide settings and preferences">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Shield className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">Admin access is required to view settings.</p>
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout title="Admin Settings" subtitle="Configure system-wide settings and preferences">
      <div className="space-y-6">

        <Tabs defaultValue="email" className="space-y-4">
          <TabsList>
            <TabsTrigger value="email" className="flex items-center gap-2" data-testid="tab-email">
              <Mail className="h-4 w-4" />
              Email Notifications
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-2" data-testid="tab-integrations">
              <Webhook className="h-4 w-4" />
              Integrations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <CardTitle>Test Mode</CardTitle>
                  {testMode.enabled && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Active
                    </Badge>
                  )}
                </div>
                <CardDescription>
                  When test mode is enabled, emails will only be sent to approved domains. 
                  Other recipients will be blocked and logged for review.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="test-mode-toggle">Enable Test Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      Restrict email sending to allowed domains only
                    </p>
                  </div>
                  <Switch
                    id="test-mode-toggle"
                    checked={testMode.enabled}
                    onCheckedChange={(checked) => setTestMode({ ...testMode, enabled: checked })}
                    data-testid="switch-test-mode"
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label>Allowed Email Domains</Label>
                  <div className="flex flex-wrap gap-2">
                    {testMode.allowedDomains.map((domain) => (
                      <Badge 
                        key={domain} 
                        variant="secondary" 
                        className="text-sm py-1 px-3"
                      >
                        {domain}
                        <button
                          onClick={() => removeDomain(domain)}
                          className="ml-2 hover:text-destructive"
                          data-testid={`remove-domain-${domain}`}
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="@example.com"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      className="max-w-xs"
                      data-testid="input-new-domain"
                    />
                    <Button variant="outline" onClick={addDomain} data-testid="button-add-domain">
                      Add Domain
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button 
                    onClick={handleSaveTestMode} 
                    disabled={updateSettingMutation.isPending}
                    data-testid="button-save-test-mode"
                  >
                    {updateSettingMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Test Mode Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <CardTitle>Email Schedule</CardTitle>
                </div>
                <CardDescription>
                  Configure when the daily authority letter notification emails are sent
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="send-hour">Send Time (Hour)</Label>
                    <Select
                      value={emailSchedule.sendHour.toString()}
                      onValueChange={(value) => setEmailSchedule({ ...emailSchedule, sendHour: parseInt(value) })}
                    >
                      <SelectTrigger id="send-hour" data-testid="select-send-hour">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => (
                          <SelectItem key={i} value={i.toString()}>
                            {i === 0 ? "12 AM" : i < 12 ? `${i} AM` : i === 12 ? "12 PM" : `${i - 12} PM`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="send-minute">Send Time (Minute)</Label>
                    <Select
                      value={emailSchedule.sendMinute.toString()}
                      onValueChange={(value) => setEmailSchedule({ ...emailSchedule, sendMinute: parseInt(value) })}
                    >
                      <SelectTrigger id="send-minute" data-testid="select-send-minute">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 15, 30, 45].map((min) => (
                          <SelectItem key={min} value={min.toString()}>
                            :{min.toString().padStart(2, '0')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select
                      value={emailSchedule.timezone}
                      onValueChange={(value) => setEmailSchedule({ ...emailSchedule, timezone: value })}
                    >
                      <SelectTrigger id="timezone" data-testid="select-timezone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button 
                    onClick={handleSaveSchedule} 
                    disabled={updateSettingMutation.isPending}
                    data-testid="button-save-schedule"
                  >
                    {updateSettingMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Schedule
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    <CardTitle>Authority Letter Template</CardTitle>
                  </div>
                  <Button 
                    onClick={handleSaveTemplate} 
                    disabled={updateSettingMutation.isPending}
                    data-testid="button-save-template"
                  >
                    {updateSettingMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Template
                  </Button>
                </div>
                <CardDescription>
                  Customize the email content sent with authority letter notifications. The body supports Markdown formatting.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-subject">Email Subject</Label>
                  <Input
                    id="email-subject"
                    value={emailTemplate.subject}
                    onChange={(e) => setEmailTemplate({ ...emailTemplate, subject: e.target.value })}
                    placeholder="Authority Letter Notification - {{workOrderNumber}}"
                    data-testid="input-email-subject"
                  />
                </div>

                <MarkdownBodyEditor
                  id="email-body"
                  value={emailTemplate.body}
                  onChange={(body) => setEmailTemplate({ ...emailTemplate, body })}
                  mergeFields={MERGE_FIELDS}
                  testIdPrefix=""
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <CardTitle>Welcome Email Template</CardTitle>
                  </div>
                  <Button 
                    onClick={handleSaveWelcomeTemplate} 
                    disabled={updateSettingMutation.isPending}
                    data-testid="button-save-welcome-template"
                  >
                    {updateSettingMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Welcome Template
                  </Button>
                </div>
                <CardDescription>
                  Customize the welcome email sent to new users when their account is created
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="welcome-email-subject">Email Subject</Label>
                  <Input
                    id="welcome-email-subject"
                    value={welcomeEmailTemplate.subject}
                    onChange={(e) => setWelcomeEmailTemplate({ ...welcomeEmailTemplate, subject: e.target.value })}
                    placeholder="Welcome to DNA Authority Management"
                    data-testid="input-welcome-email-subject"
                  />
                </div>

                <MarkdownBodyEditor
                  id="welcome-email-body"
                  value={welcomeEmailTemplate.body}
                  onChange={(body) => setWelcomeEmailTemplate({ ...welcomeEmailTemplate, body })}
                  mergeFields={WELCOME_EMAIL_MERGE_FIELDS}
                  placeholder="Enter welcome email template with merge fields and Markdown formatting..."
                  testIdPrefix="welcome-"
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <CardTitle>Watchlist Reminder Template</CardTitle>
                  </div>
                  <Button
                    onClick={handleSaveWatchlistReminderTemplate}
                    disabled={updateSettingMutation.isPending}
                    data-testid="button-save-watchlist-reminder-template"
                  >
                    {updateSettingMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Template
                  </Button>
                </div>
                <CardDescription>
                  Email sent manually to alert contacts about an active watchlist with no matches. Supports Markdown.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="watchlist-reminder-subject">Email Subject</Label>
                  <Input
                    id="watchlist-reminder-subject"
                    value={watchlistReminderTemplate.subject}
                    onChange={(e) => setWatchlistReminderTemplate({ ...watchlistReminderTemplate, subject: e.target.value })}
                    placeholder="Watchlist Reminder - {{targetIdentifier}} ({{workOrderNumber}})"
                    data-testid="input-watchlist-reminder-subject"
                  />
                </div>
                <MarkdownBodyEditor
                  id="watchlist-reminder-body"
                  value={watchlistReminderTemplate.body}
                  onChange={(body) => setWatchlistReminderTemplate({ ...watchlistReminderTemplate, body })}
                  mergeFields={WATCHLIST_EMAIL_MERGE_FIELDS}
                  placeholder="Enter watchlist reminder template with merge fields and Markdown formatting..."
                  testIdPrefix="watchlist-reminder-"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <CardTitle>Watchlist Dismissal Template</CardTitle>
                  </div>
                  <Button
                    onClick={handleSaveWatchlistDismissalTemplate}
                    disabled={updateSettingMutation.isPending}
                    data-testid="button-save-watchlist-dismissal-template"
                  >
                    {updateSettingMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Template
                  </Button>
                </div>
                <CardDescription>
                  Email sent to notify contacts when a watchlist has been canceled or expired. Supports Markdown.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="watchlist-dismissal-subject">Email Subject</Label>
                  <Input
                    id="watchlist-dismissal-subject"
                    value={watchlistDismissalTemplate.subject}
                    onChange={(e) => setWatchlistDismissalTemplate({ ...watchlistDismissalTemplate, subject: e.target.value })}
                    placeholder="Watchlist Closed - {{targetIdentifier}} ({{workOrderNumber}})"
                    data-testid="input-watchlist-dismissal-subject"
                  />
                </div>
                <MarkdownBodyEditor
                  id="watchlist-dismissal-body"
                  value={watchlistDismissalTemplate.body}
                  onChange={(body) => setWatchlistDismissalTemplate({ ...watchlistDismissalTemplate, body })}
                  mergeFields={WATCHLIST_EMAIL_MERGE_FIELDS}
                  placeholder="Enter watchlist dismissal template with merge fields and Markdown formatting..."
                  testIdPrefix="watchlist-dismissal-"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <CardTitle>Watchlist Defaults</CardTitle>
                </div>
                <CardDescription>
                  Set the default watchlist duration (in days) for new watchlists created through the Service Bureau Portal and internally.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="external-watchlist-days">Portal default watchlist days</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="external-watchlist-days"
                      type="number"
                      min="1"
                      value={externalWatchlistDays}
                      onChange={(e) => {
                        setExternalWatchlistDays(e.target.value);
                        if (externalWatchlistDaysError) setExternalWatchlistDaysError("");
                      }}
                      className="w-40"
                      data-testid="input-external-watchlist-days"
                    />
                    <Button
                      onClick={handleSaveExternalWatchlistDays}
                      disabled={updateSettingMutation.isPending}
                      data-testid="button-save-external-watchlist-days"
                    >
                      {updateSettingMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save
                    </Button>
                  </div>
                  {externalWatchlistDaysError && (
                    <p className="text-sm text-destructive">{externalWatchlistDaysError}</p>
                  )}
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="internal-watchlist-days">Internal default watchlist days</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="internal-watchlist-days"
                      type="number"
                      min="1"
                      value={internalWatchlistDays}
                      onChange={(e) => {
                        setInternalWatchlistDays(e.target.value);
                        if (internalWatchlistDaysError) setInternalWatchlistDaysError("");
                      }}
                      className="w-40"
                      data-testid="input-internal-watchlist-days"
                    />
                    <Button
                      onClick={handleSaveInternalWatchlistDays}
                      disabled={updateSettingMutation.isPending}
                      data-testid="button-save-internal-watchlist-days"
                    >
                      {updateSettingMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save
                    </Button>
                  </div>
                  {internalWatchlistDaysError && (
                    <p className="text-sm text-destructive">{internalWatchlistDaysError}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integrations" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Webhook className="h-5 w-5 text-primary" />
                  <CardTitle>Helix Webhook Configuration</CardTitle>
                </div>
                <CardDescription>
                  Configure Helix to send watchlist notifications to this URL. Copy the webhook URL and paste it into your Helix webhook subscription settings.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Environment</Label>
                    <Badge variant={webhookInfo?.environment === 'production' ? 'default' : 'secondary'}>
                      {webhookInfo?.environment || 'Loading...'}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <Label>Webhook URL</Label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 font-mono text-sm bg-muted px-3 py-2 rounded-md border overflow-x-auto">
                        {webhookInfo?.webhookUrl ? (
                          showWebhookUrl ? webhookInfo.webhookUrl : maskUrl(webhookInfo.webhookUrl)
                        ) : (
                          <span className="text-muted-foreground">Loading...</span>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setShowWebhookUrl(!showWebhookUrl)}
                        title={showWebhookUrl ? "Hide URL" : "Show URL"}
                        data-testid="button-toggle-webhook-url"
                      >
                        {showWebhookUrl ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={copyWebhookUrl}
                        disabled={!webhookInfo?.webhookUrl}
                        title="Copy URL"
                        data-testid="button-copy-webhook-url"
                      >
                        {webhookCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <Label>Webhook Secret Configured</Label>
                    <Badge variant={webhookInfo?.webhookSecretConfigured ? 'default' : 'destructive'}>
                      {webhookInfo?.webhookSecretConfigured ? 'Yes' : 'Not Configured'}
                    </Badge>
                  </div>
                </div>

                <Separator />

                <div className="bg-muted p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Setup Instructions</span>
                  </div>
                  <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                    <li>Copy the webhook URL above</li>
                    <li>Log in to your Helix account</li>
                    <li>Navigate to Webhook Subscriptions settings</li>
                    <li>Create a new subscription with the copied URL</li>
                    <li>Set the webhook secret to match the <code className="bg-background px-1 rounded">HELIX_WEBHOOK_SECRET</code> environment variable</li>
                    <li>Select the events you want to receive (e.g., watch.matched, watch.completed)</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </ResponsiveLayout>
  );
}
